import React, { useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import { NOTIFICATION_STRINGS, COMMON_STRINGS } from "../../constants/strings";

import { getNotifications, markAsRead } from "../../services/api";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

// 🔥 TYPES
import { NotificationRow } from "../../types";

import "../table-common.css";

const NotificationPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [statusFilter, setStatusFilter] = useState<
    NotificationRow["status"] | ""
  >("");

  // ================= FETCH =================
  const fetchNotifications = async () => {
    try {
      setLoading(true);

      const res = await getNotifications();
      console.log(res);
      const apiData = res?.data?.data || [];

      const formatted: NotificationRow[] = apiData.map((item: any) => {
        const name = item.actor?.name?.trim()
          ? item.actor.name
          : NOTIFICATION_STRINGS.GUEST_USER;

        const mobile = item.actor?.mobile?.trim()
          ? item.actor.mobile
          : item.meta?.mobile?.trim()
            ? item.meta.mobile
            : "-";

        return {
          id: item._id,
          title: item.title || "-",
          message: item.message || "-",
          type: item.type || "info",

          name,
          mobile, // 🔥 THIS WAS MISSING

          date: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString()
            : "-",

          status: item.isRead ? "read" : "unread",
        };
      });

      setRows(formatted);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // ================= MARK AS READ =================
  const handleMarkRead = async (id: string) => {
    try {
      // optimistic update
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, status: "read" } : row)),
      );

      await markAsRead(id);

      dispatch(
        showSnackbar({
          message: "Notification marked as read",
          type: "success",
        }),
      );
    } catch (err: any) {
      fetchNotifications();

      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    }
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();

    const searchMatch =
      row.name?.toLowerCase().includes(search) ||
      row.mobile?.includes(searchText) ||
      row.message?.toLowerCase().includes(search) ||
      row.type?.toLowerCase().includes(search);

    const statusMatch = statusFilter ? row.status === statusFilter : true;

    return searchMatch && statusMatch;
  });

  // ================= COLUMNS =================
  const columns: GridColDef<NotificationRow>[] = [
    {
      field: "name",
      headerName: NOTIFICATION_STRINGS.COLUMN_USER,
      flex: 1.5,
      renderCell: (params) =>
        params.row.name?.trim() || NOTIFICATION_STRINGS.GUEST_USER,
    },
    {
      field: "mobile",
      headerName: NOTIFICATION_STRINGS.COLUMN_CONTACT,
      flex: 1.5,
      renderCell: (params) => params.row.mobile?.trim() || "-",
    },
    {
      field: "message",
      headerName: NOTIFICATION_STRINGS.COLUMN_TITLE,
      flex: 2,
    },
    {
      field: "date",
      headerName: NOTIFICATION_STRINGS.COLUMN_DATE,
      flex: 1,
    },
    {
      field: "status",
      headerName: NOTIFICATION_STRINGS.COLUMN_STATUS,
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "actions",
      headerName: NOTIFICATION_STRINGS.COLUMN_ACTIONS,
      flex: 1.5,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const isRead = params.row.status === "read";

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<MarkEmailReadIcon />}
                disabled={isRead}
                onClick={() => handleMarkRead(params.row.id)}
                sx={{
                  backgroundColor: isRead ? "#d1d5db" : "var(--success)",
                  color: isRead ? "#888" : "#fff",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                {isRead
                  ? NOTIFICATION_STRINGS.READ
                  : NOTIFICATION_STRINGS.MARK_READ}
              </Button>
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={NOTIFICATION_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={NOTIFICATION_STRINGS.SEARCH_PLACEHOLDER}
        filterValue={statusFilter}
        onFilterChange={(value) =>
          setStatusFilter(value as NotificationRow["status"] | "")
        }
        filterOptions={[
          { label: "All", value: "" },
          {
            label: NOTIFICATION_STRINGS.STATUS_READ,
            value: "read",
          },
          {
            label: NOTIFICATION_STRINGS.STATUS_UNREAD,
            value: "unread",
          },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />
    </Box>
  );
};

export default NotificationPage;
